import React, { useState } from "react";
import PropTypes from "prop-types";
import { formatLocalDateTime } from "../../../../../utils/formatDate";
import CustomTable from "../../../components/CustomTable.jsx";
import styles from "./StrategyTable.module.css";

const COLUMNS = [
    "Estrategia",
    "Estado",
    "Journals (Sync/Total)",
    "Compliances (Sync/Total)",
    "Batches (Sync/Total)",
    "Última Extracción"
];

const SIZES = [2, 2, 2, 2, 2, 2];

const StrategyTable = ({ strategies, loading, defaultShowEmpty }) => {
    const [filter, setFilter] = useState("all");
    const [showEmpty, setShowEmpty] = useState(defaultShowEmpty);

    const filteredStrategies = strategies?.filter((strategy) => {
        // First apply the empty filter
        if (!showEmpty && strategy.extraction_summary.total === 0) {
            return false;
        }

        if (filter === "all") return true;
        if (filter === "error") return strategy.extraction_summary.journals_with_errors > 0;
        if (filter === "pending") return strategy.sync_summary.journals_pending > 0 || strategy.sync_summary.compliances_pending > 0;
        if (filter === "synced") return strategy.sync_summary.journals_pending === 0 && strategy.extraction_summary.journals_with_errors === 0 && strategy.sync_summary.compliances_pending === 0;
        return true;
    }) || [];

    const getStatusLabel = (status) => {
        if (status === "pending_with_errors") return "⚠️⏳ Pendiente con errores";
        if (status === "error") return "⚠️ Sincronizada con errores";
        if (status === "pending") return "⏳ Pendiente";
        return "✅ Sincronizada";
    };

    const tableData = filteredStrategies.map((strategy, index) => {
        const hasError = strategy.extraction_summary.journals_with_errors > 0;
        const isPending = strategy.sync_summary.journals_pending > 0 || strategy.sync_summary.compliances_pending > 0;

        let status = "success";
        if (hasError && isPending) {
            status = "pending_with_errors";
        } else if (hasError) {
            status = "error";
        } else if (isPending) {
            status = "pending";
        }

        const statusLabel = getStatusLabel(status);

        return {
            id: index.toString(),
            data: [
                <div key="strategy">
                    <div className={styles.strategyName}>{strategy.strategy_name}</div>
                    <div className={styles.strategyType}>{strategy.strategy_type}</div>
                </div>,
                <span key="status" className={styles.statusBadge}>{statusLabel}</span>,
                `${strategy.sync_summary.journals_synchronized}/${strategy.extraction_summary.total}`,
                `${strategy.sync_summary.compliances_synchronized}/${strategy.extraction_summary.compliances}`,
                `${strategy.metadata.synchronized_batches}/${strategy.metadata.batches}`,
                formatLocalDateTime(strategy.metadata.last_extraction)
            ]
        };
    });

    return (
        <div className={styles.strategyTableWrapper}>
            <CustomTable
                columns={COLUMNS}
                sizes={SIZES}
                data={tableData}
                loading={loading}
                page={1}
                totalPages={0}
                changePage={() => { }}
                renderHeader={() => (
                    <div className={styles.headerContainer}>
                        <h3 className={styles.title}>Detalle de Estrategias</h3>
                        <div className={styles.controlsContainer}>
                            <div className={styles.checkboxContainer}>
                                <input
                                    type="checkbox"
                                    id="showEmptyCheckbox"
                                    checked={showEmpty}
                                    onChange={(e) => setShowEmpty(e.target.checked)}
                                    className={styles.checkbox}
                                />
                                <label htmlFor="showEmptyCheckbox" className={styles.checkboxLabel}>
                                    Incluir Estrategias sin Journals
                                </label>
                            </div>
                            <div className={styles.filterTabs}>
                                <button
                                    className={filter === "all" ? `${styles.filterTab} ${styles.activeFilterTab}` : styles.filterTab}
                                    onClick={() => setFilter("all")}
                                >
                                    Todos
                                </button>
                                <button
                                    className={filter === "error" ? `${styles.filterTab} ${styles.activeFilterTab}` : styles.filterTab}
                                    onClick={() => setFilter("error")}
                                >
                                    Con Errores
                                </button>
                                <button
                                    className={filter === "pending" ? `${styles.filterTab} ${styles.activeFilterTab}` : styles.filterTab}
                                    onClick={() => setFilter("pending")}
                                >
                                    Pendientes
                                </button>
                                <button
                                    className={filter === "synced" ? `${styles.filterTab} ${styles.activeFilterTab}` : styles.filterTab}
                                    onClick={() => setFilter("synced")}
                                >
                                    Sincronizadas
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            />
        </div>
    );
};

StrategyTable.propTypes = {
    strategies: PropTypes.arrayOf(PropTypes.shape({
        strategy_name: PropTypes.string.isRequired,
        strategy_type: PropTypes.string.isRequired,
        extraction_summary: PropTypes.shape({
            total: PropTypes.number.isRequired,
            compliances: PropTypes.number.isRequired,
            journals_with_errors: PropTypes.number.isRequired,
        }).isRequired,
        sync_summary: PropTypes.shape({
            journals_synchronized: PropTypes.number.isRequired,
            journals_pending: PropTypes.number.isRequired,
            compliances_synchronized: PropTypes.number.isRequired,
            compliances_pending: PropTypes.number.isRequired,
        }).isRequired,
        metadata: PropTypes.shape({
            synchronized_batches: PropTypes.number.isRequired,
            batches: PropTypes.number.isRequired,
            last_extraction: PropTypes.string.isRequired,
            // last_extraction: PropTypes.instanceOf(Date).isRequired, // Or string depending on usage
        }).isRequired,
    })),
    loading: PropTypes.bool,
    defaultShowEmpty: PropTypes.bool,
};

StrategyTable.defaultProps = {
    strategies: [],
    loading: false,
    defaultShowEmpty: true,
};

export default StrategyTable;
